import { CSVLink } from "react-csv";
import { FiDownload } from "react-icons/fi";

type CSVHeader = {
  label: string;
  key: string;
};

type CSVDownloadButtonProps<T extends object> = {
  data: T[];
  headers: CSVHeader[];
  filename: string | ((data: T[]) => string);
  className?: string;
  disabled?: boolean;
};

export function CSVDownloadButton<T extends object>({
  data,
  headers,
  filename,
  className = "me-2 btn btn-outline-secondary",
  disabled = false,
}: CSVDownloadButtonProps<T>) {
  const getFilename = () => {
    if (typeof filename === "function") {
      return filename(data);
    }
    return filename;
  };

  if (disabled || data.length === 0) {
    return (
      <button
        type="button"
        className={className}
        aria-label="Download CSV"
        disabled
      >
        <FiDownload size={18} />
      </button>
    );
  }

  return (
    <CSVLink
      data={data}
      headers={headers}
      filename={getFilename()}
      className={className}
      aria-label="Download CSV"
    >
      <FiDownload size={18} />
    </CSVLink>
  );
}

// Helper function to generate name-based filenames
export function createNamedFilename<T extends object>(
  nameFields: (keyof T)[],
  baseFilename: string
) {
  return (data: T[]): string => {
    if (data.length === 0 || nameFields.length === 0) {
      return `${baseFilename}.csv`;
    }

    const firstRecord = data[0];
    const nameParts = nameFields
      .map((field) => firstRecord[field])
      .filter(Boolean)
      .map((part) => String(part).toLowerCase().trim().replace(/\s+/g, "-"))
      .join("-");

    if (!nameParts) {
      return `${baseFilename}.csv`;
    }

    return `${nameParts}-${baseFilename}.csv`;
  };
}
